/**
 * CommunicationDetail Component
 *
 * Displays the full details of a communication item with type-specific
 * content rendering:
 * - EMAIL: Sanitized HTML content
 * - SMS: Plain text with preserved whitespace
 * - NOTIFICATION: Card layout with icon and action
 * - ANNOUNCEMENT: Rich content with emphasis styling
 *
 * Shows metadata badges (ticket number, invoice number, status) when present.
 *
 * Requirements: 10.3 - Display full details when clicking on a communication
 */

import { Card, CardContent, CardHeader, CardTitle, Badge, Button } from '@bloomcode/ui';
import {
  Ticket,
  FileText,
  Bell,
  Mail,
  MessageSquare,
  Calendar,
  ArrowLeft,
  ExternalLink,
  Download,
  Phone,
} from '@bloomcode/icons';
import type { CommunicationType } from '@/components/dashboard';

export interface CommunicationDetailData {
  id: string;
  type: CommunicationType;
  title: string;
  description?: string;
  content?: string;
  date: string;
  isRead?: boolean;
  link?: string;
  metadata?: {
    ticketNumber?: string;
    invoiceNumber?: string;
    status?: string;
    amount?: number;
    currency?: string;
    dueDate?: string;
    from?: string;
    to?: string;
    attachments?: Array<{
      id: string;
      name: string;
      url: string;
      size?: number;
    }>;
  };
}

interface CommunicationDetailProps {
  communication: CommunicationDetailData | null;
  isLoading?: boolean;
  onBack?: () => void;
  onNavigateToTicket?: (ticketId: string) => void;
}

function CommunicationDetailSkeleton() {
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="space-y-2">
            <div className="h-6 w-48 animate-pulse rounded bg-muted" />
            <div className="h-4 w-32 animate-pulse rounded bg-muted" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="h-4 w-full animate-pulse rounded bg-muted" />
            <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
            <div className="h-4 w-1/2 animate-pulse rounded bg-muted" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

const communicationIcons: Record<CommunicationType, React.ReactNode> = {
  ticket: <Ticket className="h-5 w-5" />,
  invoice: <FileText className="h-5 w-5" />,
  announcement: <Bell className="h-5 w-5" />,
  email: <Mail className="h-5 w-5" />,
  message: <MessageSquare className="h-5 w-5" />,
};

const communicationColors: Record<CommunicationType, string> = {
  ticket: 'bg-primary/10 text-primary',
  invoice: 'bg-success/10 text-success',
  announcement: 'bg-accent/10 text-accent',
  email: 'bg-muted text-foreground',
  message: 'bg-warning/10 text-warning',
};

const communicationLabels: Record<CommunicationType, string> = {
  ticket: 'Support Ticket',
  invoice: 'Invoice',
  announcement: 'Announcement',
  email: 'Email',
  message: 'SMS',
};

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function formatCurrency(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount);
}

function formatFileSize(bytes?: number): string {
  if (!bytes) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  return `${size.toFixed(1)} ${units[unitIndex]}`;
}

/**
 * Sanitizes HTML content by stripping dangerous elements and attributes.
 * Allows safe formatting tags (p, br, strong, em, a, ul, ol, li, h1-h6, span, div, table, etc.)
 * Strips: script, iframe, object, embed, form, event handlers, javascript: URLs
 */
function sanitizeHtml(html: string): string {
  // Remove script tags and their content
  let sanitized = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  // Remove iframe, object, embed, form tags
  sanitized = sanitized.replace(/<(iframe|object|embed|form|link|meta)\b[^>]*\/?>/gi, '');
  sanitized = sanitized.replace(/<\/(iframe|object|embed|form|link|meta)>/gi, '');
  // Remove event handler attributes (on*)
  sanitized = sanitized.replace(/\s+on\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '');
  // Remove javascript: URLs in href/src attributes
  sanitized = sanitized.replace(
    /(href|src)\s*=\s*(?:"javascript:[^"]*"|'javascript:[^']*')/gi,
    '$1=""'
  );
  // Remove style attributes that could contain expressions
  sanitized = sanitized.replace(
    /style\s*=\s*(?:"[^"]*expression[^"]*"|'[^']*expression[^']*')/gi,
    ''
  );
  return sanitized;
}

/**
 * Checks if content appears to contain HTML tags
 */
function isHtmlContent(content: string): boolean {
  return /<[a-z][\s\S]*>/i.test(content);
}

// --- Metadata Badges Section ---

interface MetadataBadgesProps {
  metadata?: CommunicationDetailData['metadata'];
}

function MetadataBadges({ metadata }: MetadataBadgesProps) {
  if (!metadata) return null;

  const badges: Array<{
    label: string;
    value: string;
    variant: 'default' | 'secondary' | 'outline' | 'destructive';
  }> = [];

  if (metadata.ticketNumber) {
    badges.push({ label: 'Ticket', value: `#${metadata.ticketNumber}`, variant: 'outline' });
  }
  if (metadata.invoiceNumber) {
    badges.push({ label: 'Invoice', value: `#${metadata.invoiceNumber}`, variant: 'outline' });
  }
  if (metadata.status) {
    const statusVariant =
      metadata.status.toLowerCase() === 'overdue' || metadata.status.toLowerCase() === 'failed'
        ? ('destructive' as const)
        : ('secondary' as const);
    badges.push({ label: 'Status', value: metadata.status, variant: statusVariant });
  }

  if (badges.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {badges.map((badge) => (
        <Badge key={`${badge.label}-${badge.value}`} variant={badge.variant}>
          <span className="text-xs font-medium">{badge.label}:</span>
          <span className="ml-1 text-xs">{badge.value}</span>
        </Badge>
      ))}
    </div>
  );
}

// --- Type-Specific Content Renderers ---

interface ContentRendererProps {
  content: string;
}

/**
 * EMAIL content renderer — renders sanitized HTML
 */
function EmailContentRenderer({ content }: ContentRendererProps) {
  if (isHtmlContent(content)) {
    const sanitized = sanitizeHtml(content);
    return (
      <div
        className="prose prose-sm dark:prose-invert max-w-none [&_a]:text-primary [&_a]:underline"
        dangerouslySetInnerHTML={{ __html: sanitized }}
      />
    );
  }
  // Fallback: render as plain text with line breaks preserved
  return (
    <div className="prose prose-sm dark:prose-invert max-w-none">
      <p className="whitespace-pre-wrap">{content}</p>
    </div>
  );
}

/**
 * SMS content renderer — plain text with preserved whitespace
 */
function SmsContentRenderer({ content }: ContentRendererProps) {
  return (
    <div className="rounded-lg border border-border bg-muted/30 p-4">
      <div className="flex items-start gap-3">
        <Phone className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <p className="whitespace-pre-wrap text-sm leading-relaxed">{content}</p>
      </div>
    </div>
  );
}

/**
 * NOTIFICATION content renderer — card layout with emphasis
 */
function NotificationContentRenderer({ content }: ContentRendererProps) {
  return (
    <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
      <div className="flex items-start gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
          <Bell className="h-4 w-4 text-primary" />
        </div>
        <div className="flex-1">
          <p className="whitespace-pre-wrap text-sm leading-relaxed">{content}</p>
        </div>
      </div>
    </div>
  );
}

/**
 * ANNOUNCEMENT content renderer — rich content with emphasis styling
 */
function AnnouncementContentRenderer({ content }: ContentRendererProps) {
  if (isHtmlContent(content)) {
    const sanitized = sanitizeHtml(content);
    return (
      <div className="rounded-lg border border-accent/20 bg-accent/5 p-6">
        <div
          className="prose prose-sm dark:prose-invert max-w-none [&_a]:text-primary [&_a]:underline"
          dangerouslySetInnerHTML={{ __html: sanitized }}
        />
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-accent/20 bg-accent/5 p-6">
      <p className="whitespace-pre-wrap text-sm leading-relaxed">{content}</p>
    </div>
  );
}

/**
 * Default content renderer for unknown types
 */
function DefaultContentRenderer({ content }: ContentRendererProps) {
  return (
    <div className="prose prose-sm dark:prose-invert max-w-none">
      <p className="whitespace-pre-wrap">{content}</p>
    </div>
  );
}

/**
 * Routes content to the appropriate type-specific renderer
 */
function TypeSpecificContent({ type, content }: { type: CommunicationType; content: string }) {
  switch (type) {
    case 'email':
      return <EmailContentRenderer content={content} />;
    case 'message':
      return <SmsContentRenderer content={content} />;
    case 'ticket':
      return <NotificationContentRenderer content={content} />;
    case 'announcement':
      return <AnnouncementContentRenderer content={content} />;
    case 'invoice':
      return <DefaultContentRenderer content={content} />;
    default:
      return <DefaultContentRenderer content={content} />;
  }
}

// --- Type-Specific Detail Sections ---

interface TicketDetailSectionProps {
  communication: CommunicationDetailData;
  onNavigateToTicket?: (ticketId: string) => void;
}

function TicketDetailSection({ communication, onNavigateToTicket }: TicketDetailSectionProps) {
  return (
    <div className="space-y-4">
      {communication.metadata?.ticketNumber && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Ticket Number</span>
          <span className="font-mono">#{communication.metadata.ticketNumber}</span>
        </div>
      )}
      {communication.metadata?.status && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Status</span>
          <Badge variant="secondary">{communication.metadata.status}</Badge>
        </div>
      )}
      {onNavigateToTicket && communication.link && (
        <Button
          variant="outline"
          className="w-full"
          onClick={() => onNavigateToTicket(communication.link!)}
        >
          <ExternalLink className="mr-2 h-4 w-4" />
          View Full Ticket
        </Button>
      )}
    </div>
  );
}

interface InvoiceDetailSectionProps {
  communication: CommunicationDetailData;
}

function InvoiceDetailSection({ communication }: InvoiceDetailSectionProps) {
  return (
    <div className="space-y-4">
      {communication.metadata?.invoiceNumber && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Invoice Number</span>
          <span className="font-mono">#{communication.metadata.invoiceNumber}</span>
        </div>
      )}
      {communication.metadata?.amount !== undefined && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Amount</span>
          <span className="font-semibold">
            {formatCurrency(communication.metadata.amount, communication.metadata.currency)}
          </span>
        </div>
      )}
      {communication.metadata?.dueDate && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Due Date</span>
          <span>{formatDate(communication.metadata.dueDate)}</span>
        </div>
      )}
      {communication.metadata?.status && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Status</span>
          <Badge
            variant={
              communication.metadata.status.toLowerCase() === 'paid' ? 'default' : 'secondary'
            }
          >
            {communication.metadata.status}
          </Badge>
        </div>
      )}
      {communication.link && (
        <Button variant="outline" className="w-full" asChild>
          <a href={communication.link} target="_blank" rel="noopener noreferrer">
            <Download className="mr-2 h-4 w-4" />
            Download Invoice
          </a>
        </Button>
      )}
    </div>
  );
}

interface EmailDetailSectionProps {
  communication: CommunicationDetailData;
}

function EmailDetailSection({ communication }: EmailDetailSectionProps) {
  return (
    <div className="space-y-4">
      {communication.metadata?.from && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">From</span>
          <span className="text-sm">{communication.metadata.from}</span>
        </div>
      )}
      {communication.metadata?.to && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">To</span>
          <span className="text-sm">{communication.metadata.to}</span>
        </div>
      )}
    </div>
  );
}

interface AttachmentsSectionProps {
  attachments: NonNullable<CommunicationDetailData['metadata']>['attachments'];
}

function AttachmentsSection({ attachments }: AttachmentsSectionProps) {
  if (!attachments || attachments.length === 0) return null;

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium text-muted-foreground">Attachments</h4>
      <div className="space-y-2">
        {attachments.map((attachment) => (
          <a
            key={attachment.id}
            href={attachment.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 rounded-lg border p-3 transition-colors hover:bg-muted/50"
          >
            <FileText className="h-5 w-5 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{attachment.name}</p>
              {attachment.size && (
                <p className="text-xs text-muted-foreground">{formatFileSize(attachment.size)}</p>
              )}
            </div>
            <Download className="h-4 w-4 text-muted-foreground" />
          </a>
        ))}
      </div>
    </div>
  );
}

export function CommunicationDetail({
  communication,
  isLoading,
  onBack,
  onNavigateToTicket,
}: CommunicationDetailProps) {
  if (isLoading || !communication) {
    return <CommunicationDetailSkeleton />;
  }

  const icon = communicationIcons[communication.type];
  const colorClass = communicationColors[communication.type];
  const label = communicationLabels[communication.type];

  return (
    <div className="space-y-4">
      {/* Back button */}
      {onBack && (
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Communications
        </Button>
      )}

      {/* Header Card */}
      <Card>
        <CardHeader>
          <div className="flex items-start gap-4">
            <div
              className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${colorClass}`}
            >
              {icon}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline">{label}</Badge>
                {!communication.isRead && <Badge variant="default">Unread</Badge>}
              </div>
              <CardTitle className="mt-2 text-xl">{communication.title}</CardTitle>
              <div className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                <span>{formatDateTime(communication.date)}</span>
              </div>
              {/* Metadata Badges */}
              <div className="mt-3">
                <MetadataBadges metadata={communication.metadata} />
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Type-specific details */}
      {(communication.type === 'ticket' ||
        communication.type === 'invoice' ||
        communication.type === 'email') && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Details</CardTitle>
          </CardHeader>
          <CardContent>
            {communication.type === 'ticket' && (
              <TicketDetailSection
                communication={communication}
                onNavigateToTicket={onNavigateToTicket}
              />
            )}
            {communication.type === 'invoice' && (
              <InvoiceDetailSection communication={communication} />
            )}
            {communication.type === 'email' && <EmailDetailSection communication={communication} />}
          </CardContent>
        </Card>
      )}

      {/* Type-specific content rendering */}
      {communication.content && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {communication.type === 'email' ? 'Message' : 'Content'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <TypeSpecificContent type={communication.type} content={communication.content} />
          </CardContent>
        </Card>
      )}

      {/* Description fallback (if no content) */}
      {!communication.content && communication.description && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Description</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap text-sm">{communication.description}</p>
          </CardContent>
        </Card>
      )}

      {/* Attachments */}
      {communication.metadata?.attachments && communication.metadata.attachments.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <AttachmentsSection attachments={communication.metadata.attachments} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default CommunicationDetail;
